import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DefaultScheduleService } from 'app/core/services/default-schedule.service';
import { PrivateService } from 'app/core/services/private.service';
import { Subject, takeUntil } from 'rxjs';

export interface CreateWorkdayDialogData {
  C_BPartner_ID: number;
  timesheetLineID?: number;
}

interface WorkdayResult {
  message: string;
}

@Component({
  selector: 'dialog-create-workday',
  template: `
    <div class="workday-dialog">

      <div mat-dialog-title class="dialog-title">
        <mat-icon>event_available</mat-icon>
        <div>
          <h3>Create Workday</h3>
          <p>Fill in the details to create a workday record.</p>
        </div>
        <button mat-icon-button (click)="onCancel()" class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>

        <form [formGroup]="form" *ngIf="apiResult === null">

          <!-- Employee -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Employee</mat-label>
            <mat-select formControlName="employeeId"
              (selectionChange)="onEmployeeChange($event.value)"
              (openedChange)="onEmployeeDropdownOpen($event)">
              <mat-select-trigger>
                {{ getEmployeeName(form.get('employeeId')?.value) || 'Select Employee' }}
              </mat-select-trigger>
              <mat-option class="search-option">
                <input [(ngModel)]="employeeSearchText"
                  [ngModelOptions]="{standalone: true}"
                  (ngModelChange)="filterEmployees()"
                  (click)="$event.stopPropagation()"
                  placeholder="Search employee..."
                  class="search-input">
              </mat-option>
              <mat-option [value]="0">-- None --</mat-option>
              <mat-option *ngFor="let emp of filteredEmployeeList" [value]="emp.C_BPartner_ID">
                {{ emp.employeeName }}
              </mat-option>
              <mat-option *ngIf="filteredEmployeeList.length === 0 && employeeSearchText" disabled>
                <em>No employees found</em>
              </mat-option>
            </mat-select>
          </mat-form-field>

          <!-- Schedule Line -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Schedule Line</mat-label>
            <mat-select formControlName="scheduleLineId"
              (selectionChange)="onScheduleLineChange($event.value)">
              <mat-option *ngIf="isLoadingSchedules" disabled>
                <span>Loading schedules...</span>
              </mat-option>
              <mat-option *ngIf="!isLoadingSchedules && scheduleLines.length === 0" disabled>
                <em>No schedule lines found for this employee</em>
              </mat-option>
              <mat-option *ngFor="let line of scheduleLines" [value]="line.RAMP_Employee_ScheduleLine_ID">
                {{ getScheduleLabel(line) }}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="form.get('scheduleLineId')?.hasError('required')">
              Schedule Line is required.
            </mat-error>
          </mat-form-field>

          <!-- Schedule Line Details -->
          <div class="schedule-details" *ngIf="selectedScheduleLine">
            <div class="details-header">Schedule Details</div>
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Day</span>
                <span class="detail-value">{{ getWeekdayName(selectedScheduleLine.Weekday) }}</span>
              </div>
              <div class="detail-item" *ngIf="selectedScheduleLine.scheduleName">
                <span class="detail-label">Schedule</span>
                <span class="detail-value">{{ selectedScheduleLine.scheduleName }}</span>
              </div>
              <div class="detail-item" *ngIf="selectedScheduleLine.RAMP_Time1">
                <span class="detail-label">Time In</span>
                <span class="detail-value">{{ formatTime(selectedScheduleLine.RAMP_Time1) }}</span>
              </div>
              <div class="detail-item" *ngIf="selectedScheduleLine.RAMP_Time2">
                <span class="detail-label">Time Out</span>
                <span class="detail-value">{{ formatTime(selectedScheduleLine.RAMP_Time2) }}</span>
              </div>
              <div class="detail-item" *ngIf="selectedScheduleLine.RAMP_Time3">
                <span class="detail-label">Time In 2</span>
                <span class="detail-value">{{ formatTime(selectedScheduleLine.RAMP_Time3) }}</span>
              </div>
              <div class="detail-item" *ngIf="selectedScheduleLine.RAMP_Time4">
                <span class="detail-label">Time Out 2</span>
                <span class="detail-value">{{ formatTime(selectedScheduleLine.RAMP_Time4) }}</span>
              </div>
              <div class="detail-item" *ngIf="selectedScheduleLine.RAMP_Time5">
                <span class="detail-label">Time In 3</span>
                <span class="detail-value">{{ formatTime(selectedScheduleLine.RAMP_Time5) }}</span>
              </div>
              <div class="detail-item" *ngIf="selectedScheduleLine.RAMP_Time6">
                <span class="detail-label">Time Out 3</span>
                <span class="detail-value">{{ formatTime(selectedScheduleLine.RAMP_Time6) }}</span>
              </div>
            </div>
          </div>

          <!-- Unpaid Lunch Break -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Unpaid Lunch Break (minutes)</mat-label>
            <input matInput type="number" min="0" formControlName="unpaidLunchBreak">
            <mat-error *ngIf="form.get('unpaidLunchBreak')?.hasError('required')">
              This field is required.
            </mat-error>
            <mat-error *ngIf="form.get('unpaidLunchBreak')?.hasError('min')">
              Value must be 0 or greater.
            </mat-error>
          </mat-form-field>

          <!-- Unpaid Coffee Break -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Unpaid Coffee Break (minutes)</mat-label>
            <input matInput type="number" min="0" formControlName="unpaidCoffeeBreak">
            <mat-error *ngIf="form.get('unpaidCoffeeBreak')?.hasError('required')">
              This field is required.
            </mat-error>
            <mat-error *ngIf="form.get('unpaidCoffeeBreak')?.hasError('min')">
              Value must be 0 or greater.
            </mat-error>
          </mat-form-field>

        </form>

        <!-- API Result -->
        <div *ngIf="apiResult" class="alert alert-info mt-3">
          <mat-icon class="me-1">info</mat-icon>
          <p>{{ apiResult.message }}</p>
        </div>

        <!-- API Error -->
        <div *ngIf="apiError" class="alert alert-danger mt-3">
          <mat-icon class="me-1">error</mat-icon>
          <p>{{ apiError }}</p>
        </div>

      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()">
          {{ apiResult || apiError ? 'Close' : 'Cancel' }}
        </button>
        <ng-container *ngIf="apiResult === null && !apiError">
          <button mat-raised-button class="confirm-btn"
                  [disabled]="form.invalid || isLoading"
                  (click)="onConfirm()">
            <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <mat-icon *ngIf="!isLoading">check</mat-icon>
            {{ isLoading ? 'Creating...' : 'Create Workday' }}
          </button>
        </ng-container>
      </mat-dialog-actions>

    </div>
  `,
  styles: [`
    .workday-dialog { font-family: inherit; }

    [mat-dialog-title].dialog-title {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 18px 20px 14px; border-bottom: 1px solid #e2e8f0;
      background: #f8fafc; margin: 0;
    }
    .dialog-title mat-icon:first-child { color: var(--theme-primary); margin-top: 2px; flex-shrink: 0; }
    .dialog-title h3 { font-size: 1rem; font-weight: 700; margin: 0; }
    .dialog-title p { font-size: 0.75rem; color: #64748b; margin: 2px 0 0; }
    .close-btn { margin-left: auto; flex-shrink: 0; }

    mat-dialog-content {
      padding: 20px 20px 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 420px;
    }

    .full-width { width: 100%; }

    .search-input {
      width: 100%;
      border: none;
      outline: none;
      padding: 4px 8px;
      font-size: 14px;
    }

    .mat-option.search-option {
      position: sticky;
      top: 0;
      z-index: 2;
      height: auto;
      background: #fff;

      &:hover, &.mat-active { background-color: #fff !important; }
    }

    .schedule-details {
      background: var(--theme-primary-a05);
      border: 1px solid var(--theme-primary-a20);
      border-radius: 6px;
      padding: 12px 14px;
      margin-bottom: 8px;
    }
    .details-header {
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--theme-primary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 8px;
    }
    .details-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px 16px;
    }
    .detail-item { display: flex; flex-direction: column; }
    .detail-label { font-size: 0.7rem; color: #64748b; font-weight: 600; }
    .detail-value { font-size: 0.85rem; color: #1e293b; }

    .alert { display: flex; align-items: flex-start; gap: 8px; border-radius: 6px; padding: 12px 14px; }
    .alert mat-icon { flex-shrink: 0; margin-top: 2px; }
    .alert p { margin: 0; }
    .alert-info { background: #eff6ff; border: 1px solid #93c5fd; color: #1e3a5f; }
    .alert-info mat-icon { color: #2563eb; }
    .alert-danger { background: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; }
    .alert-danger mat-icon { color: #dc2626; }

    mat-dialog-actions {
      padding: 14px 20px;
      border-top: 1px solid #e2e8f0;
      background: #f8fafc;
    }

    .confirm-btn {
      background: var(--theme-primary); color: #fff;
      display: inline-flex; align-items: center; gap: 4px;
    }
    .confirm-btn:hover:not(:disabled) { background: var(--theme-primary-hover); }
    .confirm-btn:disabled { opacity: 0.5; }
  `]
})
export class DialogCreateWorkdayComponent implements OnInit, OnDestroy {

  form: FormGroup;
  isLoading = false;
  private destroy$ = new Subject<void>();
  apiResult: WorkdayResult | null = null;
  apiError: string | null = null;

  employees: any[] = [];
  filteredEmployeeList: any[] = [];
  employeeSearchText: string = '';

  scheduleLines: any[] = [];
  isLoadingSchedules = false;
  selectedScheduleLine: any = null;

  private readonly WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<DialogCreateWorkdayComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateWorkdayDialogData,
    private privateService: PrivateService,
    private defaultScheduleService: DefaultScheduleService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      employeeId: [null, Validators.required],
      scheduleLineId: [null, Validators.required],
      unpaidLunchBreak: [60, [Validators.required, Validators.min(0)]],
      unpaidCoffeeBreak: [0, [Validators.required, Validators.min(0)]],
    });

    this.privateService.getEmployeeInfo().pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        this.employees = res || [];
        this.filteredEmployeeList = [...this.employees];

        if (this.data.C_BPartner_ID) {
          this.form.patchValue({ employeeId: this.data.C_BPartner_ID });
          this.loadScheduleLines(this.data.C_BPartner_ID);
        }
      },
      error: () => {
        this.employees = [];
        this.filteredEmployeeList = [];
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filterEmployees(): void {
    const search = this.employeeSearchText?.toLowerCase() || '';
    this.filteredEmployeeList = this.employees.filter(emp =>
      emp.employeeName?.toLowerCase().includes(search)
    );
  }

  onEmployeeDropdownOpen(isOpen: boolean): void {
    if (isOpen) {
      this.employeeSearchText = '';
      this.filteredEmployeeList = [...this.employees];
    }
  }

  onEmployeeChange(employeeId: number): void {
    this.form.patchValue({ scheduleLineId: null });
    this.scheduleLines = [];
    this.selectedScheduleLine = null;
    if (employeeId && employeeId !== 0) {
      this.loadScheduleLines(employeeId);
    }
  }

  onScheduleLineChange(scheduleLineId: number): void {
    this.selectedScheduleLine = this.scheduleLines.find(
      l => l.RAMP_Employee_ScheduleLine_ID === scheduleLineId
    ) || null;
  }

  loadScheduleLines(employeeId: number): void {
    this.isLoadingSchedules = true;
    this.selectedScheduleLine = null;
    this.defaultScheduleService.getDefaultSchedulesRange().pipe(takeUntil(this.destroy$)).subscribe({
      next: (schedules: any[]) => {
        const lines: any[] = [];
        (schedules || [])
          .filter(s => s.employee === employeeId)
          .forEach(schedule => {
            const ds1 = Array.isArray(schedule.dataSource1) ? schedule.dataSource1 : [];
            ds1.forEach((line: any) => {
              lines.push({ ...line, scheduleName: schedule.description });
            });
          });
        this.scheduleLines = lines;
        this.isLoadingSchedules = false;
      },
      error: () => {
        this.scheduleLines = [];
        this.isLoadingSchedules = false;
      }
    });
  }

  getEmployeeName(id: number): string {
    if (!id || id === 0) return '';
    return this.employees.find(e => e.C_BPartner_ID === id)?.employeeName || '';
  }

  getWeekdayName(weekday: number): string {
    return this.WEEKDAYS[weekday] || '';
  }

  getScheduleLabel(line: any): string {
    const parts: string[] = [this.getWeekdayName(line.Weekday)];
    ['RAMP_Time1','RAMP_Time2','RAMP_Time3','RAMP_Time4','RAMP_Time5','RAMP_Time6'].forEach(key => {
      const t = this.formatTime(line[key]);
      if (t) parts.push(t);
    });
    return parts.join(' - ');
  }

  formatTime(time: string): string {
    if (!time) return '';
    const timePart = time.split(' ')[1];
    if (!timePart) return '';
    const [hours, minutes] = timePart.split(':');
    const period = +hours >= 12 ? 'PM' : 'AM';
    const displayHour = (+hours % 12) || 12;
    return `${displayHour}:${minutes} ${period}`;
  }

  onCancel(): void {
    this.destroy$.next();
    this.dialogRef.close(null);
  }

  onConfirm(): void {
    if (this.form.invalid) return;
    this.isLoading = true;
    const payload = {
      C_BPartner_ID: this.form.value.employeeId,
      RAMP_Employee_ScheduleLine_ID: this.form.value.scheduleLineId,
      unpaidLunchBreak: this.form.value.unpaidLunchBreak,
      unpaidCoffeeBreak: this.form.value.unpaidCoffeeBreak,
      timesheetLineID: this.data.timesheetLineID || null,
    };
    this.defaultScheduleService.create_workdays_schedule(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.apiResult = { message: res?.message || 'Request completed.' };
      },
      error: (err: any) => {
        this.isLoading = false;
        this.apiError = err?.error?.message || err?.message || 'An unexpected error occurred.';
      }
    });
  }
}
